/**
 * Email verification and password reset (docs/EMAIL_VERIFICATION_PLAN.md).
 *
 * Email/password accounts confirm their inbox with a 6-digit code. Apple and
 * Google accounts are verified by the provider and never see the code screen.
 */
import type { TFunction } from 'i18next';
import type { User } from './auth.ts';
import i18n from '../i18n.ts';

/** Dispatched when any API call answers 403 ``email_unverified``. */
export const EMAIL_UNVERIFIED_EVENT = 'auth:email-unverified';

/** Error codes the code endpoints answer with (plan §4.3). */
export type CodeError = 'invalid_code' | 'expired_code' | 'too_many_attempts';

export interface AuthResponse {
  token: string;
  user: User;
}

/** An email-only account whose email isn't verified yet. */
export function needsEmailVerification(user: User | null | undefined): boolean {
  if (user?.email_verified !== false) return false;
  const methods = user.sign_in_methods ?? [];
  return methods.length > 0 && methods.every((m) => m === 'email');
}

/** The language the server writes the email in: ``uk`` for Ukrainian, else ``en``. */
export function emailLocale(language: string | undefined = i18n.language): 'en' | 'uk' {
  return language?.toLowerCase().startsWith('uk') ? 'uk' : 'en';
}

/** True for a 403 body from the server's verification gate (plan §4.4). */
export function isEmailUnverified(status: number | undefined, body: unknown): boolean {
  return status === 403 && (body as { error?: string } | null)?.error === 'email_unverified';
}

export function notifyEmailUnverified(): void {
  globalThis.dispatchEvent(new CustomEvent(EMAIL_UNVERIFIED_EVENT));
}

const CODE_ERROR_KEYS: Record<CodeError, string> = {
  invalid_code: 'auth.code.invalid',
  expired_code: 'auth.code.expired',
  too_many_attempts: 'auth.code.tooManyAttempts',
};

interface ErrorBody {
  error?: string | string[];
  message?: string;
}

/** A readable message for a failed code / reset call. */
export function codeErrorMessage(t: TFunction, status: number, body: unknown): string {
  const data = (body ?? {}) as ErrorBody;
  const code = typeof data.error === 'string' ? data.error : undefined;
  if (code && code in CODE_ERROR_KEYS) return t(CODE_ERROR_KEYS[code as CodeError]);
  if (data.message) return data.message;
  if (Array.isArray(data.error)) return data.error.join(', ');
  return code || t('auth.errorStatus', { status });
}

/** Keep digits only, at most 6. */
export function cleanCode(value: string): string {
  return value.replaceAll(/\D/g, '').slice(0, 6);
}
